import {
  ActionType,
  EmptyListingButtonTypes,
  ExtraFilter,
  FilterAll,
  FilterComponent,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  ListingSelectOption,
  NoValue,
  Operator,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
  useNavigation,
  ViewSelectOption,
} from "o4a-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  DetailsPanelId,
  EXADB_CREATE_ROUTE,
  EXADB_FULL_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  emptyChar,
  GroupingKeys,
  ListingTestIds,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
  ViewKeys,
} from "../../constants/uiConstants";
import { AzureSubscriptionSummary } from "../../gen/clients/mchub-azure-api-client";
import {
  DatabaseSummary,
  DatabaseSummaryCollection,
  DatabaseSummaryLifecycleStateEnum,
  DatabaseSummaryStatusEnum,
  PluggableDatabaseSummary,
  PluggableDatabaseSummaryCollection,
  PluggableDatabaseSummaryLifecycleStateEnum,
  PluggableDatabaseSummaryStatusEnum,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import {
  getFieldCustomOptions,
  getStatusInfo,
  optimizedRetryOption,
  responseItemstoArray,
  statusSortComparator,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFilterStatePills } from "../../hooks/useFilterStatePills";
import { useLocationFilters } from "../../hooks/useLocationFilters";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { ExaDbDeleteCdbProps, newExaDbDeleteCdb } from "../../operations/ExaDb/ExaDbDeleteCdb";
import { ExaDbDeletePdbProps, newExaDbDeletePdb } from "../../operations/ExaDb/ExaDbDeletePdb";
import { getAzureLocationName, getOciRegion } from "../../utils";

const cdbDetailsPageRegistrationIds = PageRegistrationConfig[PageId.EXADB_CDB_DETAILS].map(config => config.key);
const pdbDetailsPageRegistrationIds = PageRegistrationConfig[PageId.EXADB_PDB_DETAILS].map(config => config.key);

const getCdbLink = (value: ExadataItem): JSX.Element => {
  if (value.containerDBName !== emptyChar) {
    return (
      <ListingDisplayNameLink
        displayName={value.containerDBName || ""}
        navigation={{
          // eslint-disable-next-line max-len
          to: `${RESOURCE_ROUTE}/${value.containerDBId}/${PageRegistrationConfig[PageId.EXADB_CDB_DETAILS][0].panelPath}?location=${value.location}`,
          pageKey: cdbDetailsPageRegistrationIds[0],
        }}
      />
    );
  }
  return <NoValue />;
};

export enum ColumnIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  ContainerDB = "containerDBName",
  FreeformTags = "freeformTags",
}

export enum ColumnTestIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  ResourceStatus = "status",
  ContainerDB = "containerDBName",
  FreeformTags = "freeformTags",
}

export enum ActionIds {
  Create = "create",
  CreateDB = "create-db",
  CreateSystem = "create-system",
  Refresh = "refresh",
  Delete = "delete",
}

export enum ActionBarTestIds {
  Create = "create",
  CreateDB = "create-db",
  CreateSystem = "create-system",
  Refresh = "refresh",
  Delete = "delete",
}

export enum FilterTestIds {
  Subscription = "Subscription",
  Location = "Location"
}

interface AzureSubscriptionSummaryMap {
  [key: string]: AzureSubscriptionSummary;
}

export interface ExadataItem {
  id: string;
  displayName: string;
  subscriptionId: string;
  subscriptionName: string;
  resourceGroupName: string;
  location: string;
  containerDBName?: string;
  containerDBId?: string;
  isPdb: boolean;
  lifecycleState: PluggableDatabaseSummaryLifecycleStateEnum | DatabaseSummaryLifecycleStateEnum;
  status: PluggableDatabaseSummaryStatusEnum | DatabaseSummaryStatusEnum;
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
  lifecycleDetails?: string;
  freeformTags?: { [key: string]: string; };
}

export const ExaDbList = (): JSX.Element => {
  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { navigateTo } = useNavigation(ConsoleContext);
  // eslint-disable-next-line max-len
  const { preferredSubscription, locale } = React.useContext<Settings>(SettingsContext);
  const filterState = React.useContext<FilterState>(FilterStateContext);
  const { listId } = useParams();

  const [selection, setSelection] = React.useState<ExadataItem[]>([]);
  const [selectedSubscription, setSelectedSubscription] = React.useState<string>("");
  const [subscriptionOptions, setSubscriptionOptions] = React.useState<{ key: string, text: string }[]>([]);

  const { locationOptions, selectedLocation, setSelectedLocation } = useLocationFilters(
    filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
  );

  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent>();

  const [filterComponentRef, setFilterComponentRef] = React.useState<FilterComponent>();
  const pillFilterValues = filterComponentRef?.getFilters();
  const filterTextValue = filterComponentRef?.getFilterText();
  const [filterText, setFilterText] = React.useState(filterState.filterTextValue || filterTextValue);

  const getLocation = (value: ExadataItem): JSX.Element => (
    <span>{getAzureLocationName(value.location)}</span>
  );

  const { perPageItemsCount } = useRowCount();

  const getStatus = (value: ExadataItem): JSX.Element => (
    <div style={{ fontSize: "14px !important" }}>
      <Status
        label={stateT(value?.status || "")}
        tooltip={stateT(value?.status || "")}
        status={getResourceLifecycleStatus(value?.status || "")}
        statusInfo={
          getStatusInfo(
            value.status,
            value.lastOperationStatus || "",
            value.lastOperationStatusDetails || "",
            value.lifecycleDetails,
          )
        }
      />
    </div>
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.DisplayName,
      testId: ColumnTestIds.DisplayName,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => {
        const isCdb = !value.isPdb;
        // eslint-disable-next-line max-len
        const cdbNavigation = `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.EXADB_CDB_DETAILS][0].panelPath}?location=${value.location}`;
        // eslint-disable-next-line max-len
        const pdbNavigation = `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.EXADB_PDB_DETAILS][0].panelPath}?location=${value.location}`;
        return (
          <ListingDisplayNameLink
            displayName={value.displayName}
            navigation={{
              to: isCdb ? cdbNavigation : pdbNavigation,
              pageKey: isCdb ? cdbDetailsPageRegistrationIds[0] : pdbDetailsPageRegistrationIds[0],
            }}
            iconName={SvgIconIds.exadataSvg}
          />
        );
      },
    },
    {
      itemProp: ColumnIds.SubscriptionName,
      testId: ColumnTestIds.SubscriptionName,
      name: Messages.labels.subscription(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureSubscriptionLink resourceId={value.id} subscriptionName={value.subscriptionName} />,
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      itemProp: ColumnIds.Location,
      testId: ColumnTestIds.Location,
      name: Messages.labels.location(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: ExadataItem,
        b: ExadataItem,
      ) => statusSortComparator<ExadataItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
    {
      itemProp: ColumnIds.ContainerDB,
      testId: ColumnTestIds.ContainerDB,
      name: Messages.labels.containerDb(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getCdbLink(value),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSelectedItems = (selectedItems: any[]): void => {
    setSelection(selectedItems);
  };

  const { refresh: cdbRefresh, loading: cdbLoading, response: cdbResponse } = useQueryCall({
    wait: !selectedSubscription || !selectedLocation,
    method: apiClients.withRegion(
      getOciRegion(selectedLocation),
    ).exaDatabaseApi.listDatabasesBySubscription,
    options: {
      args: {
        subscriptionId: selectedSubscription,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExaDbCdbs(),
      },
    },
  });

  const cdbs = cdbResponse
    && responseItemstoArray<DatabaseSummaryCollection, DatabaseSummary>(cdbResponse);

  const { refresh: pdbRefresh, loading: pdbLoading, response: pdbResponse } = useQueryCall({
    wait: !selectedSubscription || !selectedLocation,
    method: apiClients.withRegion(
      getOciRegion(selectedLocation),
    ).exaDatabaseApi.listPluggableDatabasesBySubscription,
    options: {
      args: {
        subscriptionId: selectedSubscription,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExaDbPdbs(),
      },
    },
  });

  const pdbs = pdbResponse
    && responseItemstoArray<PluggableDatabaseSummaryCollection, PluggableDatabaseSummary>(pdbResponse);

  const groupingOptions: ListingSelectOption[] = [
    {
      key: GroupingKeys.NoGrouping,
      text: Messages.listExaDb.filters.noGrouping(),
    },
  ];

  const viewOptions: ViewSelectOption[] = [
    {
      key: ViewKeys.ListView,
      text: Messages.listExaDb.filters.listView(),
      icon: MonochromeIconIds.List,
    },
  ];

  const exadataSummaryToExadataItem = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: any[],
    subscriptionsMap: AzureSubscriptionSummaryMap,
  ): ExadataItem[] => {
    const exadataItem: ExadataItem[] = [];
    items.forEach(item => {
      const { subscriptionId, resourceGroup } = parseId(item.id);
      const itemRgName = resourceGroup;
      const itemSubscriptionName = subscriptionsMap && subscriptionsMap[subscriptionId].name;
      if (item.lifecycleState !== "TERMINATED") {
        if (item.containerDatabaseId) /* is pdb */ {
          const { resourceName: cdbName } = parseId(item.containerDatabaseId);
          exadataItem.push({
            id: item.id,
            displayName: item.name,
            subscriptionId,
            subscriptionName: itemSubscriptionName,
            resourceGroupName: itemRgName,
            location: item.location,
            containerDBName: cdbName,
            containerDBId: item.containerDatabaseId,
            isPdb: true,
            lifecycleState: item.lifecycleState,
            status: item.status,
            lastOperationStatus: item.lastOperationStatus,
            lastOperationStatusDetails: item.lastOperationStatusDetails,
            lifecycleDetails: item.lifecycleDetails,
            freeformTags: item.freeformTags,
          });
        } else {
          exadataItem.push({
            id: item.id,
            displayName: item.name,
            subscriptionId,
            subscriptionName: itemSubscriptionName,
            resourceGroupName: itemRgName,
            location: item.location,
            isPdb: false,
            lifecycleState: item.lifecycleState,
            status: item.status,
            lastOperationStatus: item.lastOperationStatus,
            lastOperationStatusDetails: item.lastOperationStatusDetails,
            lifecycleDetails: item.lifecycleDetails,
            freeformTags: item.freeformTags,
          });
        }
      }
    });
    return exadataItem;
  };

  const items = React.useMemo(() => {
    let newInstance: ExadataItem[] = [];

    if (subscriptions && subscriptions.length > 0) {
      const subscriptionsMap = subscriptions.reduce((
        map: AzureSubscriptionSummaryMap,
        subscription: AzureSubscriptionSummary,
      ) => {
        map[subscription.id] = subscription;
        return map;
      }, {});
      if (cdbs) newInstance = newInstance.concat(exadataSummaryToExadataItem(cdbs, subscriptionsMap));
      if (pdbs) newInstance = newInstance.concat(exadataSummaryToExadataItem(pdbs, subscriptionsMap));
    }
    return newInstance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pdbs), JSON.stringify(cdbs), JSON.stringify(subscriptions)]);

  const onRefresh = (): void => {
    cdbRefresh();
    pdbRefresh();
  };

  const { trigger: triggerDeleteCdb } = useOperation<ExaDbDeleteCdbProps>(newExaDbDeleteCdb);
  const { trigger: triggerDeletePdb } = useOperation<ExaDbDeletePdbProps>(newExaDbDeletePdb);

  const { closePanels } = useSidePanel();

  const exaDbActions: ActionType[] = [
    {
      key: ActionIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      subMenu: {
        items: [
          {
            key: ActionIds.CreateDB,
            testId: ActionBarTestIds.CreateDB,
            text: Messages.actions.createExaDb(),
            onClick: () => {
              trackActionClick(ActionIds.CreateDB, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES);
              navigateTo(EXADB_CREATE_ROUTE, PageId.EXADB_CREATE, {
                analytics: {
                  pageId: PageId.EXA_LIST_HUB,
                  panelId: DetailsPanelId.DATABASES,
                  actionName: ActionIds.CreateDB,
                } as NavigationAnalyticsData,
              });
            },
          },
          {
            key: ActionIds.CreateSystem,
            testId: ActionBarTestIds.CreateSystem,
            text: Messages.actions.createExaSystem(),
            onClick: () => {
              trackActionClick(ActionIds.CreateSystem, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES);
              navigateTo(EXADB_FULL_CREATE_ROUTE, PageId.EXADB_CREATE, {
                analytics: {
                  pageId: PageId.EXA_LIST_HUB,
                  panelId: DetailsPanelId.DATABASES,
                  actionName: ActionIds.CreateSystem,
                } as NavigationAnalyticsData,
              });
            },
          },
        ],
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES);
        onRefresh();
        listingComponentRef?.resetTableSelection();
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      disabled: !selection.length,
      title: Messages.labels.deleteExaCSDB(),
      description: Messages.hints.deleteExaCSDBConfirmation(selection[0] ? selection[0].displayName : ""),
      onClick: () => {
        trackActionClick(ActionIds.Delete, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES);
      },
      onConfirm: () => {
        if (selection[0].isPdb) {
          triggerDeletePdb({
            databaseId: selection[0].id,
            location: selectedLocation,
            onExecute: () => {
              listingComponentRef?.resetTableSelection();
              pdbRefresh();
            },
          });
        } else {
          triggerDeleteCdb({
            databaseId: selection[0].id,
            location: selectedLocation,
            onExecute: () => {
              listingComponentRef?.resetTableSelection();
              cdbRefresh();
            },
          });
        }
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES),
    },
  ];

  React.useEffect(() => {
    const newSubscriptionOptions: { key: string, text: string }[] = [];
    // eslint-disable-next-line no-unused-expressions
    subscriptions && subscriptions.forEach(subscription => {
      newSubscriptionOptions.push({ key: subscription.id, text: subscription.name });
    });

    setSubscriptionOptions(newSubscriptionOptions);
    if (filterState.defaultSubscription) {
      setSelectedSubscription(filterState.defaultSubscription);
      if (defaultExtraFilters) defaultExtraFilters[0].value = filterState.defaultSubscription;
    } else if (preferredSubscription) {
      setSelectedSubscription(preferredSubscription);
    } else {
      setSelectedSubscription(newSubscriptionOptions.length ? newSubscriptionOptions[0].key : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subscriptions)]);

  const subscriptionFilter: ExtraFilter = {
    testId: FilterTestIds.Subscription,
    name: columns[1].name,
    operator: Operator.Equal,
    value: filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
    fieldKey: columns[1].itemProp,
    customOptions: subscriptionOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedSubscription(value);
        filterState.setDefaultSubscription(value);
      }
    },
    hideDelete: true,
  };

  const resourceGroupFilter: ExtraFilter = {
    name: columns[2].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[2].itemProp)?.value || [FilterAll],
    fieldKey: columns[2].itemProp,
    customOptions: getFieldCustomOptions(items?.map(item => item.resourceGroupName), (fieldVal: string) => fieldVal),
  };

  React.useEffect(() => {
    if (filterState.defaultLocation) {
      setSelectedLocation(filterState.defaultLocation);
      if (defaultExtraFilters) defaultExtraFilters[1].value = filterState.defaultLocation;
    } else {
      setSelectedLocation(filterState.pillFilterStateValue.filterValues?.[1]?.value || selectedLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationFilter: ExtraFilter = {
    testId: FilterTestIds.Location,
    name: columns[3].name,
    operator: Operator.Equal,
    value: filterState.defaultLocation ? filterState.defaultLocation : selectedLocation,
    fieldKey: columns[3].itemProp,
    customOptions: locationOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedLocation(value);
        filterState.setDefaultLocation(value);
      }
    },
    hideDelete: true,
  };

  const lifecycleStateFilter: ExtraFilter = {
    name: columns[4].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[4].itemProp)?.value || [FilterAll],
    fieldKey: columns[4].itemProp,
    messageCallBack: stateT,
    customOptions: getFieldCustomOptions(items?.map(item => item.status), (fieldVal: string) => stateT(fieldVal)),
  };

  const containerDbFilter: ExtraFilter = {
    name: columns[5].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[5].itemProp)?.value || [FilterAll],
    fieldKey: columns[5].itemProp,
    customOptions: getFieldCustomOptions(
      items?.map(item => item.containerDBName || ""),
      (fieldVal: string) => {
        if (fieldVal) {
          return fieldVal;
        }
        return emptyChar;
      },
    ),
  };

  const allFilterPills = [
    subscriptionFilter,
    locationFilter,
    resourceGroupFilter,
    lifecycleStateFilter,
    containerDbFilter,
  ];

  const filterPills: ExtraFilter[] = [
    subscriptionFilter,
    locationFilter,
  ];

  const textFilterCallBack = (rowItem: ExadataItem): ExadataItem => {
    const displayRowItem = { ...rowItem };
    displayRowItem.lifecycleState = stateT(
      displayRowItem.lifecycleState,
    ) as PluggableDatabaseSummaryLifecycleStateEnum | DatabaseSummaryLifecycleStateEnum;
    displayRowItem.location = getAzureLocationName(displayRowItem.location);
    return displayRowItem;
  };

  React.useEffect(() => {
    setFilterText(filterTextValue);
    filterState.setFilterTextValue(filterTextValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTextValue]);

  const defaultExtraFilters = useFilterStatePills(pillFilterValues, allFilterPills, listId, filterPills);

  const resetFilterValues = (): void => {
    const defaultPillValues = filterPills.map(filter => ({ fieldKey: filter.fieldKey, value: filter.value as string }));
    filterState.setFilterStateValue({ filterValues: defaultPillValues, listId, defaultPills: filterPills });
    filterState.setFilterTextValue("");
    setFilterText("");
  };

  return (
    <Listing
      testId={ListingTestIds.ExaDb}
      listingComponentRef={setListingComponentRef}
      items={items}
      listColumns={columns}
      itemKey={ColumnIds.DisplayName}
      itemsPerPage={perPageItemsCount}
      actionBarItems={exaDbActions}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      emptyList={{
        title: Messages.listExaDb.emptyList.title(),
        emptyButtons: [
          {
            type: EmptyListingButtonTypes.PRIMARY,
            text: Messages.actions.create(),
            menu: {
              items: [
                {
                  key: ActionIds.CreateDB,
                  text: Messages.actions.createExaDb(),
                  testId: ActionIds.CreateDB,
                  onClick: () => {
                    trackActionClick(ActionIds.CreateDB, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES);
                    navigateTo(EXADB_CREATE_ROUTE, PageId.EXADB_CREATE, {
                      analytics: {
                        pageId: PageId.EXA_LIST_HUB,
                        panelId: DetailsPanelId.DATABASES,
                        actionName: ActionIds.CreateDB,
                      } as NavigationAnalyticsData,
                    });
                  },
                },
                {
                  key: ActionIds.CreateSystem,
                  text: Messages.actions.createExaSystem(),
                  testId: ActionIds.CreateSystem,
                  onClick: () => {
                    trackActionClick(ActionIds.CreateSystem, PageId.EXA_LIST_HUB, DetailsPanelId.DATABASES);
                    navigateTo(EXADB_FULL_CREATE_ROUTE, PageId.EXADB_CREATE, {
                      analytics: {
                        pageId: PageId.EXA_LIST_HUB,
                        panelId: DetailsPanelId.DATABASES,
                        actionName: ActionIds.CreateSystem,
                      } as NavigationAnalyticsData,
                    });
                  },
                },
              ],
            },
          },
        ],
        watermark: SvgIconIds.exaWaterMarkSvg,
      }}
      isLoading={pdbLoading || cdbLoading || subscriptionLoading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.DisplayName,
      }}
      groupingSelect={{
        groupingOptions,
        defaultSelectedKey: GroupingKeys.NoGrouping,
      }}
      viewSelect={{
        viewOptions,
        defaultSelectedKey: ViewKeys.ListView,
      }}
      filtering={{
        allExtraFilters: allFilterPills,
        defaultExtraFilters,
        defaultFilterText: filterText,
        textFilterCallBack,
        componentRef: setFilterComponentRef,
        onClearFilters: resetFilterValues,
      }}
    />
  );
};
